import React, { forwardRef, useState, useImperativeHandle } from "react"
import { Modal, Box, Button, Grid, Typography, Divider, Stack } from "@mui/material"
import SearchAutoComplete from "../../SearchAutoComplete/SearchAutoComplete"
import SearchResultItem from "../../SearchResultItem/SearchResultItem"
import { useApiService } from "../../hooks/Api/useApiService"
import ModalCloseButton from "../ModalCloseButton"
import T from "../../hooks/Language/T"
import useTranslationSharie from "../../hooks/Language/useTranslation"
import { useSocket } from "../../hooks/Utils/Socket/SocketManager"

const SearchSkillModal = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    childFunction() {
      handleOpen()
    },
  }))
  const socket = useSocket()
  const [open, setOpen] = useState(false)
  const [filteredSkills, setFilteredSkills] = useState([])
  const [noResultsMessage, setNoResultsMessage] = useState("")
  const [autocompleteSelection, setAutocompleteSelection] = useState({
    name: "",
    user: "",
    cost: "",
    location: "",
    duration: "",
  })
  const [options, setOptions] = useState({
    name: [],
    user: [],
    cost: [],
    location: [],
    duration: [],
  })

  const t = useTranslationSharie()
  const { postData } = useApiService()

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setFilteredSkills([])
    setNoResultsMessage("")
    setOpen(false)
  }

  const handleOnChange = (value, field) => {
    if (!field) return
    setAutocompleteSelection((prev) => ({ ...prev, [field]: value }))
    fetchSuggestions(field, value)
  }

  const fetchSuggestions = async (field, query) => {
    if (!query) return
    try {
      const searchParams = Object.fromEntries(
        Object.entries({ ...autocompleteSelection, [field]: query }).filter(([_, val]) => val)
      )

      const data = await postData("skills/searchskills", searchParams)

      const mapData = (arr, key) => arr.map((el) => el[key] || "")

      let combinedOptions = []
      if (data?.skills) combinedOptions.push(...mapData(data.skills, field))
      if (data?.groups) {
        const groupKey = field === "user" ? "user_name" : field
        combinedOptions.push(...mapData(data.groups, groupKey))
      }

      setOptions((prev) => ({ ...prev, [field]: combinedOptions }))
    } catch (err) {
      console.error("Error fetching autocomplete options:", err)
    }
  }

  const doClear = () => {
    setAutocompleteSelection({
      name: "",
      user: "",
      cost: "",
      location: "",
      duration: "",
    })
    setFilteredSkills([])
    setOptions({
      name: [],
      user: [],
      cost: [],
      location: [],
      duration: [],
    })
    setNoResultsMessage("")
  }

  const doSearch = async () => {
    try {
      const searchParams = Object.fromEntries(
        Object.entries(autocompleteSelection).filter(([_, val]) => val)
      )

      const response = await postData("skills/searchskills", searchParams)

      const results = [...(response.skills || []), ...(response.groups || [])]

      if (results.length) {
        setFilteredSkills(results)
        sendNotification(results)
        setNoResultsMessage("")
      } else {
        setFilteredSkills([])
        setNoResultsMessage(t("No results found."))
      }
    } catch (err) {
      setFilteredSkills([])
      setNoResultsMessage(t("No results found."))
      console.error(err)
    }
  }

  const sendNotification = (skills) => {
    const fromUser = localStorage.getItem("user")
    skills.forEach((el) => {
      socket?.emit("send_notification", {
        toUserId: el.user_id,
        skill_name: el.name,
        from: fromUser,
        to: el.user_name,
        type: "search",
      })
      postData("sharie/addnotification", {
        skill_name: el.name,
        from: fromUser,
        type: "search",
      }).catch((err) => console.error(err))
    })
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <T>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            height: "90%",
            maxWidth: 1200,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ModalCloseButton handleClose={handleClose} />
          <Typography variant='h5' fontWeight='bold' gutterBottom>
            {t("Search Skills")}
          </Typography>
          <Typography variant='body2' color='textSecondary' gutterBottom>
            {t("Filter skills by multiple criteria and find users")}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Autocomplete Fields */}
          <Grid container spacing={2}>
            {["name", "user", "cost", "location", "duration"].map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field}>
                <SearchAutoComplete
                  label={t(
                    field === "name"
                      ? "Skill Name"
                      : field === "user"
                      ? "User Name"
                      : field.charAt(0).toUpperCase() + field.slice(1)
                  )}
                  options={options[field]}
                  onChange={(value) => handleOnChange(value, field)}
                />
              </Grid>
            ))}
          </Grid>

          {/* Buttons */}
          <Stack direction='row' spacing={2} sx={{ mt: 3 }}>
            <Button variant='outlined' color='secondary' onClick={doClear}>
              {t("Clear")}
            </Button>
            <Button variant='contained' color='primary' onClick={doSearch}>
              {t("Search")}
            </Button>
          </Stack>

          {/* Results */}
          <Box
            sx={{
              mt: 4,
              maxHeight: 150,
              width: "100%",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {noResultsMessage ? (
              <Typography variant='body1' color='textSecondary' align='center'>
                {noResultsMessage}
              </Typography>
            ) : (
              filteredSkills.map((skill, idx) => <SearchResultItem key={idx} skill_data={skill} />)
            )}
          </Box>
        </Box>
      </T>
    </Modal>
  )
})

export default React.memo(SearchSkillModal)
